import { useEffect, useState, useCallback } from 'react'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../contexts/AuthContext'
import { useToast, humanizeError } from '../shared/Toast'

const BLANK = {
  name: '', type: 'independent', agency_name: '',
  phone: '', email: '', schedule: '', notes: '',
}
const ACCEPT = 'application/pdf,image/jpeg,image/png'
const MAX_BYTES = 10 * 1024 * 1024

/**
 * Paid help who are not app users — independent caregivers and agency staff.
 * Distinct from `care_team_members`, which is people with a login.
 */
export default function ExternalCaregivers({ recipientId, canManage, isAdmin }) {
  const { user } = useAuth()
  const toast = useToast()
  const [list, setList] = useState([])
  const [loading, setLoading] = useState(true)
  const [unavailable, setUnavailable] = useState(false)
  const [editing, setEditing] = useState(null) // id | 'new' | null
  const [form, setForm] = useState(BLANK)
  const [file, setFile] = useState(null)
  const [busy, setBusy] = useState(false)

  const load = useCallback(async () => {
    const { data, error } = await supabase
      .from('external_caregivers')
      .select('*')
      .eq('care_recipient_id', recipientId)
      .order('created_at', { ascending: true })
    // The section hides itself rather than showing an error if the table
    // hasn't been migrated yet.
    if (error) setUnavailable(true)
    else setList(data || [])
    setLoading(false)
  }, [recipientId])

  useEffect(() => { load() }, [load])

  function startNew() {
    setForm(BLANK)
    setFile(null)
    setEditing('new')
  }
  function startEdit(c) {
    setForm({
      name: c.name || '', type: c.type || 'independent', agency_name: c.agency_name || '',
      phone: c.phone || '', email: c.email || '', schedule: c.schedule || '', notes: c.notes || '',
    })
    setFile(null)
    setEditing(c.id)
  }

  async function save() {
    if (!form.name.trim()) return toast.error('Please enter a name.')
    if (file && file.size > MAX_BYTES) return toast.error('File must be under 10MB.')
    setBusy(true)
    try {
      const payload = {
        care_recipient_id: recipientId,
        name: form.name.trim(),
        type: form.type,
        agency_name: form.type === 'agency' ? form.agency_name.trim() || null : null,
        phone: form.phone.trim() || null,
        email: form.email.trim() || null,
        schedule: form.schedule.trim() || null,
        notes: form.notes.trim() || null,
      }

      let row
      if (editing === 'new') {
        const { data, error } = await supabase
          .from('external_caregivers')
          .insert({ ...payload, created_by: user.id })
          .select()
          .single()
        if (error) throw error
        row = data
      } else {
        const { data, error } = await supabase
          .from('external_caregivers')
          .update(payload)
          .eq('id', editing)
          .select()
          .single()
        if (error) throw error
        row = data
      }

      // The upload path needs the caregiver id, so it can only happen once the
      // row exists. A failed upload leaves the caregiver saved without a plan
      // rather than losing the whole entry.
      if (file) {
        const ext = file.name.split('.').pop()
        const path = `${recipientId}/external-caregiver-plans/${row.id}/plan-${Date.now()}.${ext}`
        const { error: upErr } = await supabase.storage.from('documents').upload(path, file, { upsert: true })
        if (upErr) throw upErr
        const { error: linkErr } = await supabase
          .from('external_caregivers')
          .update({ care_plan_document_url: path })
          .eq('id', row.id)
        if (linkErr) throw linkErr
      }

      setEditing(null)
      setFile(null)
      await load()
      toast.success(editing === 'new' ? 'Caregiver added.' : 'Caregiver updated.')
    } catch (err) {
      toast.error(humanizeError(err))
    } finally {
      setBusy(false)
    }
  }

  async function remove(c) {
    if (!window.confirm(`Remove ${c.name}? This cannot be undone.`)) return
    const { error } = await supabase.from('external_caregivers').delete().eq('id', c.id)
    if (error) return toast.error(humanizeError(error))
    setList((l) => l.filter((x) => x.id !== c.id))
    toast.success('Caregiver removed.')
  }

  async function openPlan(c) {
    const { data, error } = await supabase.storage
      .from('documents')
      .createSignedUrl(c.care_plan_document_url, 3600)
    if (error || !data?.signedUrl) return toast.error(humanizeError(error || new Error('Could not open that file.')))
    window.open(data.signedUrl, '_blank', 'noopener')
  }

  if (unavailable) return null

  return (
    <div className="card stack">
      <div className="spread">
        <div className="section-title">External caregivers</div>
        {canManage && editing === null && (
          <button className="btn btn-sm btn-secondary" onClick={startNew}>+ Add</button>
        )}
      </div>

      {loading ? (
        <p className="muted" style={{ fontSize: 14, margin: 0 }}>Loading…</p>
      ) : (
        <>
          {list.length === 0 && editing !== 'new' && (
            <p className="muted" style={{ fontSize: 14, margin: 0 }}>
              No external caregivers added yet.
            </p>
          )}

          {list.map((c) =>
            editing === c.id ? (
              <CaregiverForm
                key={c.id} form={form} setForm={setForm} file={file} setFile={setFile}
                existingPlan={c.care_plan_document_url}
                onSave={save} onCancel={() => setEditing(null)} busy={busy}
              />
            ) : (
              <div key={c.id} className="stack" style={{ gap: 4, paddingBottom: 10, borderBottom: '1px solid var(--border)' }}>
                <div className="spread">
                  <div className="row" style={{ gap: 8, minWidth: 0 }}>
                    <span style={{ fontWeight: 700 }}>{c.name}</span>
                    <span className="badge">
                      {c.type === 'agency' ? (c.agency_name || 'Agency') : 'Independent'}
                    </span>
                  </div>
                  {canManage && (
                    <div className="row" style={{ gap: 10, flexShrink: 0 }}>
                      <button onClick={() => startEdit(c)} style={{ background: 'none', border: 'none', color: 'var(--teal)', fontSize: 13 }}>Edit</button>
                      {isAdmin && (
                        <button onClick={() => remove(c)} style={{ background: 'none', border: 'none', color: 'var(--danger)', fontSize: 13 }}>Remove</button>
                      )}
                    </div>
                  )}
                </div>
                {c.phone && <a href={`tel:${c.phone}`} style={{ fontSize: 14 }}>{c.phone}</a>}
                {c.schedule && <div className="muted" style={{ fontSize: 13 }}>🗓 {c.schedule}</div>}
                {c.notes && <div className="muted" style={{ fontSize: 13, lineHeight: 1.5 }}>{c.notes}</div>}
                {c.care_plan_document_url && (
                  <button
                    onClick={() => openPlan(c)}
                    style={{ background: 'none', border: 'none', padding: 0, color: 'var(--teal)', fontWeight: 600, fontSize: 13, textAlign: 'left' }}
                  >
                    📄 View their care plan →
                  </button>
                )}
              </div>
            )
          )}

          {editing === 'new' && (
            <CaregiverForm
              form={form} setForm={setForm} file={file} setFile={setFile}
              onSave={save} onCancel={() => setEditing(null)} busy={busy}
            />
          )}
        </>
      )}
    </div>
  )
}

function CaregiverForm({ form, setForm, file, setFile, existingPlan, onSave, onCancel, busy }) {
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }))
  return (
    <div className="stack" style={{ gap: 8, padding: '10px 0' }}>
      <input className="input" placeholder="Name *" value={form.name} onChange={(e) => set('name', e.target.value)} aria-label="Caregiver name" />

      <div className="row wrap" style={{ gap: 8 }}>
        <button
          type="button" className={`chip${form.type === 'independent' ? ' chip-on' : ''}`}
          aria-pressed={form.type === 'independent'} onClick={() => set('type', 'independent')}
        >
          Independent
        </button>
        <button
          type="button" className={`chip${form.type === 'agency' ? ' chip-on' : ''}`}
          aria-pressed={form.type === 'agency'} onClick={() => set('type', 'agency')}
        >
          From an agency
        </button>
      </div>

      {form.type === 'agency' && (
        <input className="input" placeholder="Agency name" value={form.agency_name} onChange={(e) => set('agency_name', e.target.value)} aria-label="Agency name" />
      )}

      <div className="row" style={{ gap: 8 }}>
        <input className="input grow" type="tel" inputMode="tel" placeholder="Phone" value={form.phone} onChange={(e) => set('phone', e.target.value)} aria-label="Phone" />
        <input className="input grow" type="email" inputMode="email" placeholder="Email" value={form.email} onChange={(e) => set('email', e.target.value)} aria-label="Email" />
      </div>

      <input className="input" placeholder="e.g. Mon/Wed/Fri 9am–1pm" value={form.schedule} onChange={(e) => set('schedule', e.target.value)} aria-label="Schedule" />
      <input className="input" placeholder="e.g. Specializes in dementia care" value={form.notes} onChange={(e) => set('notes', e.target.value)} aria-label="Notes" />

      <div className="field">
        <label className="label">
          Upload their care plan <span className="hint">— optional, PDF or image</span>
        </label>
        <input type="file" accept={ACCEPT} onChange={(e) => setFile(e.target.files?.[0] || null)} />
        {file && <span className="badge mt-4">📎 {file.name}</span>}
        {!file && existingPlan && (
          <span className="muted" style={{ fontSize: 12 }}>A care plan is already on file. Choosing a file replaces it.</span>
        )}
      </div>

      <div className="row" style={{ gap: 8 }}>
        <button className="btn btn-ghost" onClick={onCancel} disabled={busy}>Cancel</button>
        <button className="btn grow" onClick={onSave} disabled={busy}>
          {busy ? <span className="spinner" /> : 'Save'}
        </button>
      </div>
    </div>
  )
}
